/**
 * DHCP Leases Tab
 *
 * Displays active DHCP leases from the Cloud Gateway agent.
 * Read-only view with refresh capability.
 */

import { useQuery } from '@tanstack/react-query'
import {
  ServerIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  MonitorIcon,
} from 'lucide-react'

import { gatewayApi } from '@nebula/shared/services'

export default function DHCPLeasesTab() {
  const {
    data: leasesResult,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['gateway-dhcp-leases'],
    queryFn: gatewayApi.getDHCPLeases,
    refetchInterval: 30_000,
  })

  const leases = (leasesResult?.result?.leases || []) as any[]

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center space-x-2">
          <ServerIcon className="size-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-gray-900">DHCP Leases</h3>
          {leases.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
              {leases.length} ativo{leases.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
        >
          <RefreshCwIcon className={`mr-1.5 size-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Leases Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-6 animate-spin text-purple-600" />
        </div>
      ) : leasesResult?.error ? (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangleIcon className="size-5 shrink-0 text-red-500" />
          <span className="text-sm text-red-700">{leasesResult.error}</span>
        </div>
      ) : leases.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <MonitorIcon className="mx-auto mb-3 size-10 text-gray-300" />
          <p className="text-sm">Nenhum lease DHCP ativo.</p>
          <p className="mt-1 text-xs text-gray-400">
            Leases aparecem quando dispositivos obtêm IP via DHCP do gateway.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  IP
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  MAC Address
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Hostname
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Expira em
                </th>
              </tr>
            </thead>
            <tbody>
              {leases.map((lease: any, index: number) => (
                <tr
                  key={`${lease.mac}-${index}`}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-3 py-2 font-mono text-gray-900">{lease.ip}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">{lease.mac}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {lease.hostname || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {lease.expires ? (
                      formatExpiry(lease.expires)
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function formatExpiry(expires: string | number): string {
  try {
    const num = typeof expires === 'number' ? expires : Number(expires)
    // dnsmasq returns Unix timestamp (seconds); ISO strings won't parse as a finite number
    const date = isFinite(num) && num > 0 ? new Date(num * 1000) : new Date(expires)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()

    if (isNaN(diffMs)) return String(expires)
    if (diffMs <= 0) return 'Expirado'

    const diffMins = Math.floor(diffMs / 60_000)
    const diffHours = Math.floor(diffMins / 60)

    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`
    return `${diffMins}m`
  } catch {
    return String(expires)
  }
}
